
import Image from "./Image";
import { CardLink, useInshurLanding } from "../landingMode";
import { ArrowRight } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { PricingPennant } from "./PricingPennant";
import { CardSlideToggle } from "./HomeCardSlideToggle";
import { RentShieldCardLogo } from "./RentShieldCardLogo";
export function MobilePriceBadge({ label, price, unit }) {
    return (<span className="inline-flex items-baseline gap-1.5 rounded-full border border-white/25 bg-black/40 px-3 py-1.5 font-sans text-xs text-white shadow-lg backdrop-blur-md">
      <span className="font-medium text-white/70">{label}</span>
      <span className="text-sm font-semibold">{price}</span>
      <span className="text-white/60">{unit}</span>
    </span>);
}
/* `active` / `onSelect` / `forceReveal` / `flushTop` are optional carousel hooks. With no props
   the card renders standalone; the carousel passes them to drive the bottom-left slide toggle,
   the reveal, and to drop the standalone top margin. */
export function HomeAiCard({ active = 0, onSelect, forceReveal = false, flushTop = false, cobrand = false, } = {}) {
    const inshur = useInshurLanding(); // /inshur: card is not a link, no "Learn more"
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const revealed = forceReveal || inView;
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, { threshold: 0.35 });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return (<section ref={ref} className={`mx-auto w-full max-w-7xl px-5 sm:px-8 md:px-16${flushTop ? "" : " mt-8 sm:mt-12 lg:mt-16"}`} aria-labelledby="ai-heading">
      <CardLink href="/rentshield" className={`group relative isolate block overflow-hidden rounded-2xl transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]${revealed ? " ai-reveal" : ""}`} style={{
            /* RentShield, using the same radial corner-bloom as the RentID card: a vivid
               coral-pink glowing from the top-left corner (behind the logo + heading), fading
               through magenta and deep indigo down to near-black (matching the reference). */
            backgroundColor: "#050505",
            backgroundImage: "radial-gradient(115% 95% at 10% 12%, #FF5C7A 0%, #C42463 28%, #5E1C48 54%, #1C0B18 80%, #050505 100%)",
        }}>
        {/* Black glass sheen across the top edge — a soft frosted band that fades down,
            giving the card a sense of depth behind the logo and pennants. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-10 h-40 bg-gradient-to-b from-black/70 via-black/25 to-transparent backdrop-blur-md [mask-image:linear-gradient(to_bottom,black_45%,transparent)]"/>

        {/* Light top-edge stroke — a pronounced highlight tracing the rounded top edge,
            layered above the sheen so it stays visible (matches the green card). */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-30 rounded-2xl border-t border-white/35"/>

        {/* RentShield lockup, pinned to the top-left corner. On scroll-in a specular
            highlight sweeps across the mark (clipped to the artwork via an image mask). */}
        <RentShieldCardLogo cobrand={cobrand}/>

        {/* Pricing pennants — top right corner, matching the Turo page. */}
        <div className="pointer-events-none absolute right-6 -top-px z-20 hidden items-start gap-3 sm:flex">
          <PricingPennant label="Off rent" price="$37" unit="/vehicle/mo"/>
          <PricingPennant label="On rent" price="$16" unit="/vehicle/day"/>
        </div>

        <div className="flex flex-col gap-8 px-6 pb-32 pt-6 sm:px-8 sm:pb-28 sm:pt-8 lg:flex-row lg:items-stretch lg:gap-6">
          <div className="flex flex-1 flex-col">
            <div className="mt-auto pt-20 sm:-translate-y-[30px] sm:pt-24 lg:pt-32">
              <span className="mb-3 block font-sans text-xs font-semibold uppercase tracking-[0.16em] text-white/70">
                Commercial Auto Insurance
              </span>
              {/* Title reveals word-by-word on scroll-in: each word rises, unblurs and
            fades in, staggered by its index (see `.ai-word` in globals.css). Slower
            and more pronounced than a single all-at-once fade. */}
              <h3 key={active} id="ai-heading" className="ai-card-title font-sans text-3xl font-normal sm:text-4xl md:text-[44px] leading-[1.1] tracking-tight text-white text-balance">
                {"On rent insurance built into the booking process".split(" ").map((word, i, arr) => (<Fragment key={`${word}-${i}`}>
                    <span className="ai-word" style={{ ["--i"]: i }}>
                      {word}
                    </span>
                    {i < arr.length - 1 ? " " : ""}
                  </Fragment>))}
              </h3>
              {/* Fixed min-height so this block and the roadside card's block are the same
            height, keeping the bottom-anchored eyebrow/title/subcopy at an identical
            vertical position across both cards. */}
              <p className="mt-4 max-w-md font-sans text-base sm:min-h-[7.5rem] sm:text-lg leading-relaxed text-white/60 text-pretty">
                Renters can choose from on rent liability or full coverage insurance plans directly from the checkout. Primary, A-rated, insurance to protect your business, vehicles, and direct or gig drivers.
              </p>
            </div>
          </div>

          {/* Mobile only: the pennants become compact price badges above the vehicle. */}
          <div className="flex flex-wrap gap-2 sm:hidden">
            <MobilePriceBadge label="Off rent" price="$37" unit="/vehicle/mo"/>
            <MobilePriceBadge label="On rent" price="$16" unit="/vehicle/day"/>
          </div>

          {/* Vehicle image. */}
          <div className="relative -my-10 flex flex-1 items-center justify-center sm:-mt-36 sm:mb-0 sm:min-h-72 lg:my-0">
            <Image src="/images/home/card-ai-vehicle.webp" alt="A white SUV lit from behind, available to rent" width={520} height={520} sizes="(max-width: 1024px) 90vw, 45vw" className="h-auto w-full max-w-md origin-center scale-[1.35] object-contain mix-blend-lighten sm:translate-y-8"/>
          </div>
        </div>

        {/* Bottom bar: slide toggle (Insurance / Roadside) on the left, Learn more on the
            right. The toggle only renders inside the carousel (when onSelect is provided). */}
        <div className="absolute inset-x-6 bottom-6 z-20 flex flex-col items-start gap-4 sm:inset-x-8 sm:bottom-8 sm:flex-row sm:items-center sm:justify-between">
          {onSelect ? <CardSlideToggle active={active} onSelect={onSelect}/> : <span />}
          {inshur ? <span /> : (
          <span className="inline-flex items-center gap-2 text-base font-normal text-white sm:text-lg">
            <span className="underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
              Learn more
            </span>
            <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
          </span>
          )}
        </div>
      </CardLink>
    </section>);
}
